import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { useUpdateCategory } from '@/hooks/use-categories';
import { describeError } from '@/lib/api-client';
import type { Category } from '@/types/api';

/**
 * Merge is expressed as a PATCH with `mergeIntoSlug`: every link on `source` moves to the
 * target category and the source is retired by the API.
 */
export function MergeCategoryDialog({
  open,
  onOpenChange,
  source,
  categories,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: Category;
  categories: Category[];
}) {
  const updateCategory = useUpdateCategory();
  const { toast } = useToast();
  const [targetSlug, setTargetSlug] = useState('');

  useEffect(() => {
    if (!open) setTargetSlug('');
  }, [open]);

  const targets = categories.filter(
    (category) => category.slug !== source.slug && !category.archived,
  );
  const target = targets.find((category) => category.slug === targetSlug);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!target || updateCategory.isPending) return;

    updateCategory.mutate(
      { categoryId: source.categoryId, body: { mergeIntoSlug: target.slug } },
      {
        onSuccess: () => {
          toast({
            title: 'Categories merged',
            description: `“${source.name}” now points at “${target.name}”.`,
            variant: 'success',
          });
          onOpenChange(false);
        },
        onError: (error) =>
          toast({
            title: 'Could not merge the categories',
            description: describeError(error),
            variant: 'error',
          }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Merge “{source.name}”</DialogTitle>
          <DialogDescription>
            Every link currently in “{source.name}” is moved to the category you pick, and “
            {source.name}” is retired. This cannot be undone automatically.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="merge-target">Merge into</Label>
            {targets.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                There is no other active category to merge into.
              </p>
            ) : (
              // Radix treats an empty string as a real value; undefined means "nothing picked".
              <Select value={targetSlug || undefined} onValueChange={setTargetSlug}>
                <SelectTrigger id="merge-target">
                  <SelectValue placeholder="Choose a category…" />
                </SelectTrigger>
                <SelectContent>
                  {targets.map((category) => (
                    <SelectItem key={category.categoryId} value={category.slug}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!target} loading={updateCategory.isPending}>
              Merge categories
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
